package com.schoolconnect.app.repository.documents

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.response.DocumentLibraryListResponse
import com.schoolconnect.app.datasource.network.request.DocumentCreateRequest
import com.schoolconnect.app.datasource.network.request.DocumentUpdateRequest
import com.schoolconnect.app.datasource.network.request.DocumentClassTargetRequest
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.feature.common.PageRequest
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.util.scLog
import com.schoolconnect.app.repository.common.RepositoryCache
import com.schoolconnect.app.repository.common.tenantCacheKey
import kotlinx.coroutines.CoroutineScope

object DocumentLibraryRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] DocumentLibraryRepository"
    private val pageCache = RepositoryCache<DocumentLibraryListResponse>()

    suspend fun loadDocument(documentId: String): Result<com.schoolconnect.app.datasource.network.response.DocumentResponseDTO> {
        val normalizedDocumentId = documentId.trim()
        if (normalizedDocumentId.isBlank()) return Result.failure(IllegalArgumentException("documentId is required"))
        return KtorApiClient.getDocument(normalizedDocumentId)
    }

    suspend fun loadMetrics() = KtorApiClient.getDocumentMetrics()

    suspend fun update(documentId: String, request: DocumentUpdateRequest): Result<com.schoolconnect.app.datasource.network.response.DocumentResponseDTO> =
        KtorApiClient.updateDocument(documentId.trim(), request).onSuccess { pageCache.clear() }

    suspend fun getDocumentDownloadUrl(documentId: String): Result<String> =
        KtorApiClient.getDocumentDownloadUrl(documentId.trim()).fold(
            onSuccess = { response ->
                response.url?.trim()?.takeIf { it.isNotBlank() }?.let { Result.success(it) }
                    ?: Result.failure(IllegalStateException("The document download URL was not returned."))
            },
            onFailure = { Result.failure(it) }
        )

    suspend fun loadPage(
        query: String = "",
        category: String? = null,
        applicableTo: String? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sortBy: String = "updated_at",
        order: String = "desc",
        forceRemote: Boolean = false,
        refreshScope: CoroutineScope,
    ): Result<DocumentLibraryListResponse> {
        val request = PageRequest(pageIndex, pageSize)
        val normalizedQuery = query.trim().takeIf { it.isNotBlank() }
        val normalizedCategory = category?.trim()?.takeIf { it.isNotBlank() }
        val normalizedApplicableTo = applicableTo?.trim()?.takeIf { it.isNotBlank() }
        scLog(
            "$TAG loadPage query=${normalizedQuery ?: "<blank>"} category=${normalizedCategory ?: "<any>"} " +
                "applicableTo=${normalizedApplicableTo ?: "<any>"} pageIndex=${request.safePageIndex} " +
                "pageSize=${request.safePageSize}"
        )
        val normalizedSort = sortBy.trim().takeIf { it.isNotBlank() }
        val normalizedOrder = order.trim().lowercase().takeIf { it == "asc" || it == "desc" }
        val key = tenantCacheKey("documents:$normalizedQuery:$normalizedCategory:$normalizedApplicableTo:${request.offset}:${request.safePageSize}:$normalizedSort:$normalizedOrder")
        return pageCache.load(refreshScope, TAG, "loadPage", key, forceRemote = forceRemote) { KtorApiClient.listDocuments(
            query = normalizedQuery,
            category = normalizedCategory,
            applicableTo = normalizedApplicableTo,
            limit = request.safePageSize,
            offset = request.offset,
            sortBy = normalizedSort,
            order = normalizedOrder
        ).onSuccess { result ->
            scLog("$TAG loadPage complete pageIndex=${request.safePageIndex} items=${result.items?.size ?: 0} total=${result.count ?: "<unknown>"}")
        }.onFailure { throwable ->
            scLog("$TAG loadPage failure pageIndex=${request.safePageIndex}", throwable)
        } }
    }

    suspend fun loadParentPage(
        studentId: String,
        query: String = "",
        category: String? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sortBy: String = "updated_at",
        order: String = "desc",
    ): Result<DocumentLibraryListResponse> {
        val normalizedStudentId = studentId.trim()
        if (normalizedStudentId.isBlank()) return Result.failure(IllegalArgumentException("studentId is required"))
        val request = PageRequest(pageIndex, pageSize)
        return KtorApiClient.listDocuments(
            query = query.trim().takeIf { it.isNotBlank() },
            category = category?.trim()?.takeIf { it.isNotBlank() && it != "All Categories" },
            limit = request.safePageSize,
            offset = request.offset,
            sortBy = sortBy,
            order = order,
            studentId = normalizedStudentId
        ).onSuccess { result ->
            scLog("$TAG loadParentPage studentId=$normalizedStudentId pageIndex=${request.safePageIndex} items=${result.items?.size ?: 0} total=${result.count ?: "<unknown>"}")
        }.onFailure { throwable ->
            scLog("$TAG loadParentPage failure studentId=$normalizedStudentId pageIndex=${request.safePageIndex}", throwable)
        }
    }

    suspend fun getParentDocument(documentId: String, studentId: String): Result<com.schoolconnect.app.datasource.network.response.DocumentResponseDTO> =
        KtorApiClient.getDocument(documentId.trim(), studentId.trim())

    suspend fun getParentDocumentDownloadUrl(documentId: String, studentId: String): Result<String> =
        KtorApiClient.getDocumentDownloadUrl(documentId.trim(), studentId.trim()).fold(
            onSuccess = { response ->
                response.url?.trim()?.takeIf { it.isNotBlank() }?.let { Result.success(it) }
                    ?: Result.failure(IllegalStateException("The document download URL was not returned."))
            },
            onFailure = { Result.failure(it) }
        )

    suspend fun create(
        schoolId: String,
        title: String,
        description: String,
        category: String,
        applicableTo: String,
        filename: String,
        contentType: String,
        bytes: ByteArray,
        classTargetIds: List<String> = emptyList()
    ): Result<Unit> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) return Result.failure(IllegalArgumentException("schoolId is required"))
        if (title.trim().isBlank()) return Result.failure(IllegalArgumentException("Document title is required"))
        if (title.trim().length > 500) return Result.failure(IllegalArgumentException("Document title must be 500 characters or fewer"))
        if (description.length > 2000) return Result.failure(IllegalArgumentException("Description must be 2000 characters or fewer"))
        if (bytes.isEmpty()) return Result.failure(IllegalArgumentException("File must not be empty"))
        if (bytes.size > 50 * 1024 * 1024) return Result.failure(IllegalArgumentException("File must be 50 MB or smaller"))
        val fileId = FileUploadRepository.uploadStandalone("DOCUMENT", filename, contentType, bytes).getOrElse { return Result.failure(it) }
        val createResult = KtorApiClient.createDocument(
            DocumentCreateRequest(
                schoolId = normalizedSchoolId,
                title = title,
                description = description,
                category = category,
                applicableTo = applicableTo,
                classTargets = classTargetIds.mapNotNull { it.trim().takeIf(String::isNotBlank) }
                    .distinct()
                    .takeIf { it.isNotEmpty() }
                    ?.map(::DocumentClassTargetRequest),
                fileId = fileId
            )
        )
        if (createResult.isFailure) {
            FileUploadRepository.delete(fileId).onFailure { cleanupError ->
                scLog("$TAG create cleanup failure fileId=$fileId", cleanupError)
            }
            return Result.failure(createResult.exceptionOrNull() ?: IllegalStateException("Unable to create document"))
        }
        pageCache.clear()
        return Result.success(Unit)
    }
}
